import { Button, Card, Container, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { EldDriver, EldVehicle } from "src/models";
import useModelData, { deleteById, replaceById } from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import useModelSavingData from "src/hooks/use-model-saving-data";

import PageHeader from "src/components/page-header";
import Label from "src/components/label";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import { EldDriverModal } from "./_eld_driver_modal";


type EldDriversProps = {
  eldDrivers?: EldDriver[],
  onEldDriverSelected: (a: EldDriver) => void
}

const searchMapper = (eldDriver: EldDriver) => [eldDriver.suspended ? "Suspended" : ""];

const EldDrivers = memo(({ eldDrivers, onEldDriverSelected }: EldDriversProps) => {
  const { sortedList, sortData } = useSort(eldDrivers, "firstName");

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["firstName", "lastName", "username", "email", "driverNumber", "phone", "driverLicenseNumber", "driverLicenseState", "hosRule", "cargoType", "restBreak"], undefined, searchMapper);

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="ELD Drivers" />

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortKey="driverNumber" sortData={sortData} >
                Driver Number
              </SortableHeader>

              <SortableHeader sortKey="firstName" sortData={sortData} >
                Name
              </SortableHeader>

              <SortableHeader sortKey="username" sortData={sortData} >
                Username
              </SortableHeader>

              <SortableHeader sortKey="email" sortData={sortData} >
                Email
              </SortableHeader>

              <SortableHeader sortKey="eldVehicleUnitNumber" sortData={sortData}>
                Assigned Vehicle
              </SortableHeader>
            </TableRow>
          </TableHead>


          <TableBody>
            {filteredList.map((eldDriver, index) =>
            (<TableRow
              key={index}
              hover
              onClick={() => onEldDriverSelected(eldDriver)}
              sx={{ cursor: "pointer" }}
            >

              <TruxTableCell>
                {eldDriver.driverNumber}
              </TruxTableCell>

              <TruxTableCell>
                {eldDriver.firstName} {eldDriver.lastName}
                {eldDriver.suspended && <Label sx={{ ml: 1, color: "white", background: "#29b6f6" }}>Suspended</Label>}
              </TruxTableCell>

              <TruxTableCell>
                {eldDriver.username}
              </TruxTableCell>

              <TruxTableCell>
                {eldDriver.email}
              </TruxTableCell>

              <TruxTableCell>
                {eldDriver.eldVehicleUnitNumber}
              </TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  eldDrivers?: EldDriver[],
  eldVehicles?: EldVehicle[],
}

export default function Page() {
  const settings = useSettingsContext();
  const router = useRouter();
  const dataLoader = useCallback(() => ({
    eldDrivers: axios.get(endpoints.eldDrivers.findAllInactive).then(response => response.data),
    eldVehicles: axios.get(endpoints.eldVehicles.findAll).then(response => response.data),
  }), []);
  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  const [isOpen, setIsOpen] = useState(false);

  const selectedEldDriverModel = useModelSavingData<EldDriver>(
    endpoints.eldDrivers.update,
    endpoints.eldDrivers.create
  );

  const updateEldDriver = () => {
    selectedEldDriverModel.save().then(response => {
      if (response) {
        enqueueSnackbar("Successfully saved the Eld Driver");
        const eldDriver = response.data as EldDriver;
        if (eldDriver.active) {
          updateData("eldDrivers")(deleteById(data!.eldDrivers!, eldDriver))
        } else {
          updateData("eldDrivers")(replaceById(data!.eldDrivers!, eldDriver));
        }

        setIsOpen(false);
      }
    })
  }

  const onEldDriverSelected = useCallback((a: EldDriver) => {
    selectedEldDriverModel.set({
      ...a, newPassword: "",
      newPasswordConfirm: ""
    });
    setIsOpen(true);
  }, [selectedEldDriverModel, setIsOpen]);


  const eldDriversButton = <Button
    onClick={() => router.push("/app/eld/eld-drivers/list")}
    variant="soft"
  >
    Active Drivers
  </Button>;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.eldDrivers?.length !== 0 &&
          <PageHeader title="Inactive ELD Drivers">
            {eldDriversButton}
          </PageHeader>
        }

        {data && data?.eldDrivers?.length === 0 &&
          <NoneFound title="No Inactive ELD Drivers Found" message="To go to the ELD Drivers page click the button below.">
            {eldDriversButton}
          </NoneFound>
        }

        {data && data?.eldDrivers?.length !== 0 && <EldDrivers eldDrivers={data.eldDrivers} onEldDriverSelected={onEldDriverSelected} />}

        {data && <EldDriverModal
          selectedEldDriverModel={selectedEldDriverModel}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          updateEldDriver={updateEldDriver}
          eldVehicles={data.eldVehicles || []}
        />}
      </DisplayPageData>
    </Container >
  );
}
